import { usePageView } from '@fs/zion-analytics'
import { Button, Cell, Grid, ResponsiveTableRow, Row, Select, TextField, H6 } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveTable } from '../../common/DmcResponsiveTable'
import { usePageTitle } from '../../app/PageTitleContext'
import { toLocaleString } from '../../../util/DateUtil'
import { CtrLink } from '../../app/Links'
import { DmcDatePicker } from '../../common/DmcDatePicker/DmcDatePicker'
import DmcTemplePicker from '../../common/DmcTemplePicker'
import { RawData } from '../../common/RawData'
import { dateFormats, useCtrSearchState } from './useCtrSearchState'
import { getLastPathElement } from '../../../util/StringUtil'

export default function notUsed() {}

export function CtrSearch() {
  usePageView({ page_detail: 'DMConsole:CtrSearch' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.search.ctr'))
  const { ctrSearchState, ctrSearchActions } = useCtrSearchState()
  return (
    <Grid>
      {/* Row */}
      <Cell columns={12}>
        <SearchForm ctrSearchState={ctrSearchState} ctrSearchActions={ctrSearchActions} />
      </Cell>

      {/* Row */}
      <Cell columns={12}>
        <SearchResults ctrSearchState={ctrSearchState} ctrSearchActions={ctrSearchActions} />
      </Cell>

      {/* Row */}
      <Cell columns={12}>
        <RawData state={ctrSearchState} />
      </Cell>
    </Grid>
  )
}

function SearchForm({ ctrSearchState, ctrSearchActions }) {
  const { t } = useTranslation()
  return (
    <>
      <Grid>
        {/* Row */}
        <Cell columns={6}>
          <TextField
            id="name"
            label={t('name', 'Name')}
            value={ctrSearchState.formState.name}
            onChange={(event) => ctrSearchActions.setName(event.target.value)}
            helpMessage={t(
              'search.ctr.hint',
              "Hint: Can use wildcard character '*'. Example: 'John*' will return 'John' and 'john doe'."
            )}
            warningValidate={() =>
              ctrSearchState.formState.name ? null : t('search.nameIsRequired', 'Name is Required')
            }
          />
        </Cell>
        <Cell columns={3}>
          <DmcDatePicker
            idType="earliest"
            label={t('search.earliestPerformedDate', 'Earliest Performed Date')}
            warningValidate={() =>
              ctrSearchActions.getDateWarningMessage(ctrSearchState?.formState?.earliestPerformedDate)
            }
            selectedItem={ctrSearchState.formState.earliestPerformedDate}
            setSelectedItem={ctrSearchActions.setEarliestDate}
            dayMonthYearDateFormat={dateFormats.CTR_SEARCH_ENDPOINT}
          />
        </Cell>
        <Cell columns={3}>
          <DmcDatePicker
            idType="latest"
            label={t('search.latestPerformedDate', 'Latest Performed Date')}
            warningValidate={
              ctrSearchState?.formState?.templeCode && ctrSearchState?.formState?.earliestPerformedDate?.primaryText
                ? () => ctrSearchActions.getDateWarningMessage(ctrSearchState?.formState?.latestPerformedDate)
                : undefined
            }
            disabled={
              !ctrSearchState?.formState?.templeCode || !ctrSearchState?.formState?.earliestPerformedDate?.formattedDate
            }
            selectedItem={ctrSearchState.formState.latestPerformedDate}
            setSelectedItem={ctrSearchActions.setLatestDate}
            dayMonthYearDateFormat={dateFormats.CTR_SEARCH_ENDPOINT}
          />
        </Cell>

        {/*  Row */}
        <Cell columns={4}>
          <Select
            id="ordinanceType"
            name="ordinanceType"
            label={t('odm.ordinanceType', 'Ordinance Type')}
            placeholder={t('search.selectOrdinanceType', 'Select Ordinance Type')}
            options={ctrSearchState.formMetadata.ordinanceTypeOptions}
            value={ctrSearchState.formState.ordinanceType}
            onChange={(event) => ctrSearchActions.setOrdinanceType(event.target.value)}
            warningValidate={() =>
              ctrSearchState.formState.ordinanceType
                ? null
                : t('search.ordinanceTypeIsRequired', 'Ordinance Type is Required')
            }
          />
        </Cell>
        <Cell columns={4}>
          <DmcTemplePicker
            templeCode={ctrSearchState.formState.templeCode}
            onSuggestionSelected={(selection) => selection && ctrSearchActions.setTemple(selection)}
            onClear={ctrSearchActions.clearTemple}
          />
        </Cell>
        <Cell columns={4}>
          <Select
            id="officialType"
            name="officialType"
            label={t('ctr.officialType', 'Official Type')}
            placeholder={t('search.ctr.selectOfficialType', 'Select Official Type')}
            options={ctrSearchState.formMetadata.officialTypeOptions}
            value={ctrSearchState.formState.officialType}
            onChange={(event) => ctrSearchActions.setOfficialType(event.target.value)}
          />
        </Cell>
      </Grid>
      <Row alignX="end">
        <Button
          emphasis="high"
          onClick={ctrSearchActions.search}
          disabled={
            !ctrSearchState?.formState?.name ||
            !ctrSearchState?.formState?.earliestPerformedDate?.formattedDate ||
            !ctrSearchState?.formState?.latestPerformedDate?.formattedDate ||
            !ctrSearchState?.formState?.ordinanceType
          }
        >
          {t('ctr.search', 'Search')}
        </Button>
        <Button emphasis="medium" onClick={ctrSearchActions.reset}>
          {t('ctr.clear', 'Clear')}
        </Button>
      </Row>
    </>
  )
}

function SearchResults({ ctrSearchState }) {
  const { t } = useTranslation()

  if (!ctrSearchState.searchResults) {
    return null
  }
  if (ctrSearchState.searchResults.length === 0) {
    return <div>{t('noResultsFound', 'No Results Found')}</div>
  }
  const abbreviations = {
    BAPTISM: 'B',
    CONFIRMATION: 'C',
    INITIATORY: 'I',
    ENDOWMENT: 'E',
    SEALING_TO_PARENTS: 'SP',
    SEALING_TO_SPOUSE: 'SS',
    LDS_LICENSE_MARRIAGE: 'L',
    TIME_ONLY_MARRIAGE: 'T',
    MELCHIZEDEK_PRIESTHOOD_ORDINATION: 'M',
  }

  return (
    <>
      <H6>{`${ctrSearchState.searchResults.length} ${t('results', 'Results').toLowerCase()}`}</H6>
      <div data-testid="responsiveTableBaseElement">
        <DmcResponsiveTable
          separator={<br />}
          atSizeColumns={{
            default: {
              labels: [
                t('ctrId', 'CTR ID'),
                t('principalName', 'Principal Name'),
                t('odm.ordinance.type', 'Ordinance Type'),
                t('ctr.performedDate', 'Performed Date'),
                t('templeCode', 'Temple Code'),
                t('ctr.officialType', 'Official Type'),
                t('status.label', 'Status'),
                `${t('type', 'Type')}, ${t('date', 'Date')} & ${t('place', 'Place')}`,
              ],
              columnWidths: [1, 2, 1, 2, 1, 2, 1, 2],
            },
            sm: {
              labels: [
                `${t('ctrId', 'CTR ID')}, ${t('principalName', 'Principal Name')}, ${t(
                  'odm.ordinance.type',
                  'Ordinance Type'
                )} & ${t('ctr.performedDate', 'Performed Date')}`,
                `${t('templeCode', 'Temple Code')}, ${('ctr.officialType', 'Official Type')}, ${t(
                  'status.label',
                  'Status'
                )}, ${t('type', 'Type')}, ${t('date', 'Date')} & ${t('place', 'Place')}`,
              ],
              columnWidths: [6, 6],
              combinationRules: [
                ['column1', 'column2', 'column3', 'column4'],
                ['column5', 'column6', 'column7', 'column8', 'column9', 'column10'],
              ],
            },
            md: {
              labels: [
                `${t('ctrId', 'CTR ID')}, ${t('principalName', 'Principal Name')} & ${t(
                  'odm.ordinance.type',
                  'Ordinance Type'
                )}`,
                `${t('ctr.performedDate', 'Performed Date')}, ${t('templeCode', 'Temple Code')} & ${
                  ('ctr.officialType', 'Official Type')
                }`,
                `${t('status.label', 'Status')}, ${t('type', 'Type')}, ${t('date', 'Date')} & ${t('place', 'Place')}`,
              ],
              columnWidths: [4, 4, 4],
              combinationRules: [
                ['column1', 'column2', 'column3'],
                ['column4', 'column5', 'column6'],
                ['column7', 'column8', 'column9', 'column10'],
              ],
            },
            lg: {
              labels: [
                `${t('ctrId', 'CTR ID')}, ${t('principalName', 'Principal Name')} & ${t(
                  'odm.ordinance.type',
                  'Ordinance Type'
                )}`,
                `${t('ctr.performedDate', 'Performed Date')}, ${t('templeCode', 'Temple Code')} & ${
                  ('ctr.officialType', 'Official Type')
                }`,
                `${t('status.label', 'Status')}, ${t('type', 'Type')}, ${t('date', 'Date')} & ${t('place', 'Place')}`,
              ],
              columnWidths: [4, 4, 4],
              combinationRules: [
                ['column1', 'column2', 'column3'],
                ['column4', 'column5', 'column6'],
                ['column7', 'column8', 'column9', 'column10'],
              ],
            },
            xl: {
              labels: [
                `${t('ctrId', 'CTR ID')}, ${t('principalName', 'Principal Name')} & ${t(
                  'odm.ordinance.type',
                  'Ordinance Type'
                )}`,
                `${t('ctr.performedDate', 'Performed Date')}, ${t('templeCode', 'Temple Code')} & ${
                  ('ctr.officialType', 'Official Type')
                }`,
                `${t('status.label', 'Status')}, ${t('type', 'Type')}, ${t('date', 'Date')} & ${t('place', 'Place')}`,
              ],
              columnWidths: [4, 4, 4],
              combinationRules: [
                ['column1', 'column2', 'column3'],
                ['column4', 'column5', 'column6'],
                ['column7', 'column8', 'column9', 'column10'],
              ],
            },
            xxl: {
              labels: [
                t('ctrId', 'CTR ID'),
                t('principalName', 'Principal Name'),
                t('odm.ordinance.type', 'Ordinance Type'),
                t('ctr.performedDate', 'Performed Date'),
                t('templeCode', 'Temple Code'),
                t('ctr.officialType', 'Official Type'),
                t('status.label', 'Status'),
                `${t('type', 'Type')}, ${t('date', 'Date')} & ${t('place', 'Place')}`,
              ],
              columnWidths: [1, 2, 1, 2, 1, 2, 1, 2],
              combinationRules: [
                ['column1'],
                ['column2'],
                ['column3'],
                ['column4'],
                ['column5'],
                ['column6'],
                ['column7'],
                ['column8', 'column9', 'column10'],
              ],
            },
          }}
          totalColumnCount={10}
        >
          {ctrSearchState.searchResults.map((item) => {
            const ctrData = ctrSearchState?.ctrData?.[item.ctrId]
            return (
              <ResponsiveTableRow
                key={item.ctrId}
                column1={<CtrLink ctrId={item.ctrId} otherTab />}
                column2={item.fullNames[0]}
                column3={abbreviations[item.ordinanceType]}
                column4={toLocaleString(new Date(item.performedDate), false)}
                column5={item.templeCode}
                column6={item.officialType}
                column7={item.ordinanceStatus}
                column8={getLastPathElement(ctrData?.type)}
                column9={ctrData?.date.original}
                column10={ctrData?.place.original}
              />
            )
          })}
        </DmcResponsiveTable>
      </div>
    </>
  )
}
